import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { PriorityBadge } from "@/components/brand/PriorityBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CompleteActivityDialog from "@/components/activities/CompleteActivityDialog";
import { CheckSquare, Loader2, CheckCircle2, ExternalLink, AlertOctagon } from "lucide-react";

const ACT_TYPES = ["Telefonata", "Richiamo", "Follow-up preventivo", "Rinnovo contratto", "Appuntamento", "Preparazione preventivo", "Modifica preventivo", "WhatsApp", "Email", "Altro"];
const ACT_STATUS = ["Da fare", "Completata", "Scaduta"];
const statusTone = (s) => (s === "Completata" ? "success" : s === "Scaduta" ? "danger" : "pending");

export default function ActivitiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("Da fare");
  const [type, setType] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [operators, setOperators] = useState([]);
  const [operatorId, setOperatorId] = useState("");
  const [complete, setComplete] = useState(null);

  useEffect(() => { if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {}); }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (type) params.type = type;
      if (overdue) params.overdue = true;
      if (operatorId) params.operator_id = operatorId;
      const { data } = await api.get("/activities", { params });
      setRows(data);
    } catch (e) { setRows([]); } finally { setLoading(false); }
  }, [status, type, overdue, operatorId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="activities-page">
      <div>
        <h1 className="ds-page-title flex items-center gap-2"><CheckSquare className="h-6 w-6 text-brand" /> Attività & Follow-up</h1>
        <p className="mt-1 text-sm text-slate-500">Follow-up preventivi, richiami ed esiti contatto. Nessun cliente dimenticato.</p>
      </div>

      <div className="ds-card flex flex-wrap items-end gap-3 p-4">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px]" data-testid="act-status"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti gli stati</SelectItem>{ACT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="act-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti i tipi</SelectItem>{ACT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => setOverdue((v) => !v)} data-testid="act-overdue" className={`rounded-full border px-3 py-1.5 text-sm font-medium ${overdue ? "border-danger bg-danger/10 text-danger" : "border-border text-slate-600"}`}>Solo scaduti</button>
        {isAdmin && (
          <Select value={operatorId || "all"} onValueChange={(v) => setOperatorId(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid="act-operator"><SelectValue placeholder="Operatore" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <div className="ds-card overflow-hidden">
        {loading ? <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
          : rows.length === 0 ? <div className="flex flex-col items-center justify-center gap-2 py-16 text-slate-500"><CheckSquare className="h-8 w-8 text-slate-300" /> Nessuna attività con questi filtri.</div>
          : (
            <div className="divide-y divide-slate-100">
              {rows.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-3 hover:bg-brand/5" data-testid={`activity-row-${a.id}`}>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-800">{a.customer_name}</span>
                      <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold text-brand">{a.type}</span>
                      <StatusBadge type={statusTone(a.status)}>{a.status}</StatusBadge>
                      <PriorityBadge level={a.priority} />
                      {a.is_overdue && <span className="inline-flex items-center gap-1 text-xs font-semibold text-danger"><AlertOctagon className="h-3 w-3" /> scaduta</span>}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-x-4 text-xs text-slate-500">
                      <span>{new Date(a.activity_date).toLocaleDateString("it-IT")}{a.activity_time ? ` · ${a.activity_time}` : ""}</span>
                      {a.quote_number && <span>Preventivo {a.quote_number}</span>}
                      {a.notes && <span className="truncate">{a.notes}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {a.status !== "Completata" && <Button size="sm" variant="ghost" onClick={() => setComplete(a)} title="Registra esito" data-testid={`activity-complete-${a.id}`}><CheckCircle2 className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/clienti/${a.customer_id}`)} title="Apri cliente" data-testid={`activity-open-${a.id}`}><ExternalLink className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {complete && <CompleteActivityDialog open={!!complete} onOpenChange={(v) => !v && setComplete(null)} activity={complete} onSaved={() => { setComplete(null); load(); }} />}
    </div>
  );
}
